import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { useNavigate } from "react-router-dom";
import { CheckCircle, ArrowRight } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { pageTemplates } from "../utils/pageTemplates";
import { LEDIGE_INTRO, LEDIGE_FAQ } from "../data/homeContent";
import { INK, PRIMARY, Overline, AuroraBlue, AuroraLight, Reveal, MagneticButton } from "../components/shared/kursusUi";

const getSection = (sections, type) => sections?.find(s => s.type === type);

const UnemployedPage = () => {
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/pages/ledige`);
      setPageData(response.data);
    } catch (error) {
      setPageData(pageTemplates["ledige"]);
    }
  };

  const data = pageData || pageTemplates["ledige"];
  const hero = getSection(data.sections, 'hero');
  const benefits = getSection(data.sections, 'benefits');
  const steps = getSection(data.sections, 'steps');
  const cta = getSection(data.sections, 'cta');


  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'hsl(210, 20%, 98%)' }}>
      <div className="votra-grain" aria-hidden />
      <Header />

      {/* Hero — dark ink + blue aurora */}
      <section className="hero-light relative overflow-hidden" style={{ backgroundColor: '#F4F6FB' }}>
        <AuroraLight />
        <div className="fx-scan"><span /></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10 py-20 md:py-28">
          <div className="max-w-3xl">
            <Reveal><Overline light>Ledig</Overline></Reveal>
            <Reveal i={1}>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-white leading-[1.05] mb-6">
                {hero?.title || "Ledig? Kom i job i transportbranchen"}
              </h1>
            </Reveal>
            <Reveal i={2}>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8 max-w-2xl">
                {hero?.description}
              </p>
            </Reveal>
            <Reveal i={3}>
              <MagneticButton
                onClick={() => navigate('/')}
                className="kursus-glow-btn inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-white"
                style={{ backgroundColor: PRIMARY }}
              >
                Se kurser <ArrowRight className="h-5 w-5" />
              </MagneticButton>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-6 py-16 md:py-20 flex-1">
        <div className="max-w-4xl mx-auto space-y-10">
          {/* Benefits */}
          {benefits && (
            <Reveal className="bg-white rounded-3xl border border-slate-200/80 p-8 md:p-10 shadow-[0_8px_30px_rgba(15,40,90,0.06)]">
              <Overline>Fordele</Overline>
              <h3 className="font-display text-2xl md:text-3xl font-medium mb-6 text-slate-900">
                {benefits.title}
              </h3>
              <div className="space-y-4">
                {benefits.items?.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 flex-shrink-0" style={{ color: PRIMARY }} />
                    <p className="text-slate-700 leading-relaxed">{typeof benefit === 'string' ? benefit : benefit.title}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          )}

          {/* Steps */}
          {steps && (
            <Reveal className="bg-white rounded-3xl border border-slate-200/80 p-8 md:p-10 shadow-[0_8px_30px_rgba(15,40,90,0.06)]">
              <Overline>Sådan kommer du i gang</Overline>
              <h3 className="font-display text-2xl md:text-3xl font-medium mb-8 text-slate-900">
                {steps.title}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {steps.items?.map((step, idx) => (
                  <div key={idx} className="text-center">
                    <div className="w-14 h-14 rounded-2xl text-white font-display font-medium text-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: PRIMARY }}>
                      {step.number}
                    </div>
                    <h4 className="font-semibold mb-2 text-slate-900">{step.title}</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">{step.description}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          )}

          {/* CTA */}
          {cta && (
            <Reveal className="relative overflow-hidden rounded-3xl p-10 text-center text-white" style={{ backgroundColor: INK }}>
              <AuroraBlue />
              <div className="relative z-10">
                <h3 className="font-display text-2xl md:text-3xl font-medium mb-4">{cta.title}</h3>
                <p className="text-slate-300 mb-8 max-w-2xl mx-auto">{cta.description}</p>
                <MagneticButton
                  onClick={() => navigate('/')}
                  className="kursus-glow-btn px-7 py-3.5 rounded-full font-semibold text-white"
                  style={{ backgroundColor: PRIMARY }}
                >
                  {cta.buttonText}
                </MagneticButton>
              </div>
            </Reveal>
          )}
        </div>
      </div>

      {/* SEO content + FAQ */}
      <section className="bg-white border-t border-slate-100" data-testid="ledige-seo-content">
        <div className="container mx-auto px-4 md:px-6 py-20 max-w-4xl">
          <Reveal>
            <Overline>Godt at vide</Overline>
            <h2 className="font-display text-3xl md:text-4xl font-medium mb-6 text-slate-900 leading-tight">{LEDIGE_INTRO.heading}</h2>
          </Reveal>
          <div className="space-y-5 text-slate-600 leading-relaxed text-lg">
            {LEDIGE_INTRO.paragraphs.map((p, i) => <Reveal as="p" i={i} key={i}>{p}</Reveal>)}
          </div>
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl font-medium mt-16 mb-8 text-slate-900 leading-tight">Ofte stillede spørgsmål</h2>
          </Reveal>
          <div className="divide-y divide-slate-200 border-t border-slate-200">
            {LEDIGE_FAQ.map((f, i) => (
              <Reveal i={i} key={i} className="py-6" data-testid={`ledige-faq-${i}`}>
                <h3 className="text-lg font-semibold mb-2 flex items-start gap-3 text-slate-900">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: PRIMARY }} />
                  {f.q}
                </h3>
                <p className="text-slate-600 leading-relaxed pl-[18px]">{f.a}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UnemployedPage;
